import type { DemoSummary, Product } from "../../../shared/types";
import { api, type ProductWithDemos } from "../../api";
import {
  BrandLogo, IconBolt, IconCapture, IconChevronDown, IconDownload, IconMonitor, IconMoon, IconPlus,
  IconSearch, IconSignOut, IconSun, IconUser,
} from "../../icons";
import Menu from "./Menu";

export const DEMO_TABS = [
  { key: "basics", label: "Settings / Basics" },
  { key: "outcomes", label: "Paths" },
  { key: "studio", label: "Studio" },
  { key: "survey", label: "Survey" },
  { key: "preview", label: "Preview" },
  { key: "analytics", label: "Analytics" },
] as const;

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const modKey = isMac ? "⌘" : "Ctrl";

export interface Where {
  /** "demo" inside a demo, "page" everywhere else. */
  kind: "demo" | "page";
  product?: Product | ProductWithDemos;
  demo?: DemoSummary;
  tab?: string;
  /** The current non-demo page: shown after "Workspace /". */
  page?: string;
}

interface Props {
  where: Where;
  products: ProductWithDemos[];
  go: (to: string) => void;
  onSearch: () => void;
  theme: string;
  setTheme: (t: string) => void;
  onLogout: () => void;
  /** Who is signed in (Clerk). */
  email?: string;
  /** Opens Clerk's account screen: profile, password, security. */
  onManageAccount?: () => void;
}

const THEMES = [
  { key: "system", label: "System", icon: <IconMonitor /> },
  { key: "light", label: "Light", icon: <IconSun /> },
  { key: "dark", label: "Dark", icon: <IconMoon /> },
];

/** A link that navigates through the guarded router instead of reloading. */
function A({ to, go, className, children, ...rest }: { to: string; go: (to: string) => void; className?: string; children: React.ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a href={to} className={className} {...rest} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; e.preventDefault(); go(to); }}>{children}</a>;
}

export default function TopBar({ where, products, go, onSearch, theme, setTheme, onLogout, email, onManageAccount }: Props) {
  const demoBase = where.demo ? `/admin/demos/${where.demo.id}` : "";
  const live = where.demo?.status === "published";
  const tabs = where.kind === "demo"
    ? DEMO_TABS.map((t) => ({ key: t.key, label: t.label, to: `${demoBase}/${t.key}` }))
    : [{ key: "demos", label: "Demos", to: "/admin" }, { key: "captures", label: "Captures", to: "/admin/captures" }];
  const activeTab = where.kind === "demo" ? where.tab : where.page === "Captures" ? "captures" : where.page === "Demos" || !where.page ? "demos" : "";

  return (
    <header className="topbar">
      <A to="/admin" go={go} className="brand" aria-label="DemoDive home"><BrandLogo alt="" /></A>
      <span className="tb-sep" aria-hidden />

      <div className="tb-crumbs">
        {where.kind === "demo" && where.demo && where.product ? (
          <>
            <A to={`/admin/products/${where.product.id}`} go={go} className="tb-crumb">{where.product.name}</A>
            <span className="tb-slash" aria-hidden>/</span>
            <Menu label="Switch demo" className="tb-switch" triggerClass="tb-demo" button={<><span className="tb-demo-title">{where.demo.title}</span><span className={`pill ${live ? "pill-live" : "pill-draft"}`}>{live ? "Live" : "Draft"}</span><IconChevronDown className="icon icon-xs" /></>}>
              {(close) => (
                <div className="pop-body">
                  {products.map((p) => (
                    <div key={p.id} className="pop-group">
                      <div className="eyebrow pop-label">{p.name}</div>
                      {p.demos.length === 0 && <p className="small muted pop-empty">No demos yet.</p>}
                      {p.demos.map((d) => (
                        <button key={d.id} role="menuitem" className={`pop-item ${d.id === where.demo!.id ? "on" : ""}`} onClick={() => { close(); go(`/admin/demos/${d.id}/${where.tab ?? "studio"}`); }}>
                          <span>{d.title}</span>{d.status === "published" && <span className="live-dot" title="Live" />}
                        </button>
                      ))}
                    </div>
                  ))}
                  <div className="pop-sep" />
                  <button role="menuitem" className="pop-item" onClick={() => { close(); go("/admin"); }}>All demos</button>
                  <button role="menuitem" className="pop-item" onClick={() => { close(); go("/admin/new"); }}><IconPlus className="icon icon-xs" />New product</button>
                </div>
              )}
            </Menu>
          </>
        ) : (
          <>
            <A to="/admin" go={go} className="tb-crumb">Workspace</A>
            {where.page && where.page !== "Demos" && <><span className="tb-slash" aria-hidden>/</span><span className="tb-crumb-current">{where.page}</span></>}
          </>
        )}
      </div>

      <nav className="tb-tabs" aria-label={where.kind === "demo" ? "Demo sections" : "Workspace"}>
        {tabs.map((t) => (
          <A key={t.key} to={t.to} go={go} className={`tb-tab ${activeTab === t.key ? "on" : ""}`} aria-current={activeTab === t.key ? "page" : undefined}>{t.label}</A>
        ))}
      </nav>

      <span className="spacer" />

      <button type="button" className="tb-search" onClick={onSearch} aria-label="Search and jump (command menu)">
        <IconSearch /><span className="tb-search-text">Search</span><kbd>{modKey} K</kbd>
      </button>

      {where.kind === "demo" && where.demo ? (
        <A to={`${demoBase}/publish`} go={go} className="btn btn-primary tb-publish"><IconBolt />Publish &amp; Share</A>
      ) : (
        <A to="/admin/new" go={go} className="btn btn-primary tb-publish"><IconPlus />New product</A>
      )}

      <Menu label="Account and preferences" align="right" triggerClass="tb-avatar" button={<IconUser />}>
        {(close) => (
          <div className="pop-body">
            {email && <><div className="pop-account"><span className="eyebrow">Signed in as</span><span className="pop-email" title={email}>{email}</span></div><div className="pop-sep" /></>}
            <div className="eyebrow pop-label">Theme</div>
            <div className="theme-switch" role="radiogroup" aria-label="Theme">
              {THEMES.map((t) => (
                <button key={t.key} type="button" role="radio" aria-checked={theme === t.key} className={theme === t.key ? "on" : ""} onClick={() => setTheme(t.key)}>{t.icon}{t.label}</button>
              ))}
            </div>
            <div className="pop-sep" />
            <button role="menuitem" className="pop-item" onClick={() => { close(); go("/admin/captures"); }}><IconCapture className="icon" />Captures</button>
            <button role="menuitem" className="pop-item" onClick={() => { close(); api.downloadExport().catch((e) => alert(e.message)); }}><IconDownload className="icon" />Export all data (JSON)</button>
            {onManageAccount && <button role="menuitem" className="pop-item" onClick={() => { close(); onManageAccount(); }}><IconUser className="icon" />Manage account</button>}
            <button role="menuitem" className="pop-item" onClick={() => { close(); onLogout(); }}><IconSignOut className="icon" />Sign out</button>
          </div>
        )}
      </Menu>
    </header>
  );
}
